import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, useDisclosure, useToast } from '@bonusly/design-system';

import type { AttachDoodlePayload, DoodleCard } from '../../../../lib/bizy/types/ui-blocks';
import { DoodleModal, DoodlePreview, hasRenderedContent, rasterizeStrokes, type DoodleStroke } from '../../doodle';
import { useBizyChatActions, useBizyUiBlockActionDispatcher } from '../context/actions-context';
import { useBizyDoodleUpload } from '../context/doodle-upload-context';
import type { BizyNodeProps } from './node-props';

/**
 * Standalone doodle card (node type `doodle_card`). Emitted after the user
 * clicks "Add a doodle" on the recognition draft. Shows the generated doodle,
 * lets the user edit it, then rasterizes + uploads it and dispatches
 * `attach_doodle` so the backend re-emits the recognition draft with the
 * doodle attached and a `doodleRationale` caption.
 */
export function DoodleCardNode({ node }: BizyNodeProps) {
	const block = node as DoodleCard;
	const { t } = useTranslation('admin_bizy');
	const dispatch = useBizyUiBlockActionDispatcher();
	const { isReadOnly } = useBizyChatActions();
	const uploadDoodle = useBizyDoodleUpload();
	const doodleModal = useDisclosure();
	const toast = useToast();

	const { strokes: initialStrokes, rationale, draft } = block.payload;
	const [strokes, setStrokes] = useState<DoodleStroke[]>(initialStrokes ?? []);
	const [attachPending, setAttachPending] = useState(false);
	// Terminal success state: the doodle has been dispatched and the button
	// settles into a disabled "Added to draft" label. The backend re-emit is a
	// NEW chat message (this card never unmounts), so we can't rely on unmount
	// to signal success — we flip this flag the moment the dispatch succeeds.
	const [attached, setAttached] = useState(false);
	// One-shot guard so "Add to recognition" can't double-fire while the
	// async rasterize/upload is in flight.
	const attachGuard = useRef(false);

	const showAttachError = () => {
		toast({ description: t('doodle.attach_error'), status: 'error', duration: 5000, isClosable: true });
	};

	const resetAttachState = () => {
		attachGuard.current = false;
		setAttachPending(false);
	};

	const handleSaveEdit = (nextStrokes: DoodleStroke[]) => {
		setStrokes(nextStrokes);
	};

	// Map the draft fields the backend needs to re-emit the recognition draft
	// card, mirroring the shape `attach_gif` sends (minus the gif-only fields).
	const draftFields = () => ({
		recipient: draft.recipient ?? null,
		amount: draft.amount ?? null,
		message: draft.message,
		hashtags: draft.hashtags ?? [],
		actions: draft.actions ?? [],
		...(draft.variants ? { variants: draft.variants } : {}),
		...(draft.coachingPrompt ? { coachingPrompt: draft.coachingPrompt } : {}),
	});

	const handleAddToRecognition = async () => {
		// Gate on rendered content, not array length: a doodle of degenerate
		// taps/flicks would otherwise upload a blank white PNG.
		if (attachGuard.current || !hasRenderedContent(strokes)) return;
		attachGuard.current = true;
		setAttachPending(true);
		try {
			const png = await rasterizeStrokes(strokes);
			const { url } = await uploadDoodle(png);
			const payload: AttachDoodlePayload = {
				...draftFields(),
				// Mirror the PNG url onto `mp4` as well as `gif` (like ImageAttachment
				// does): the give-box renders social attachments from `mp4`, so an
				// empty `mp4` would render the doodle as a broken image there.
				attachment: { gif: url, mp4: url, altText: 'doodle' },
				doodleRationale: rationale ?? null,
			};
			dispatch({ type: 'attach_doodle', payload });
			// Success: settle the button into a terminal disabled "Added to draft"
			// state immediately. Keep the guard armed so it can't re-fire. The
			// backend re-emit is a new chat message, so this card stays mounted.
			setAttachPending(false);
			setAttached(true);
		} catch (error) {
			// Surface a user-visible error and re-arm so the user can retry after a
			// transient rasterize/upload failure. Deliberately do NOT re-throw:
			// this runs unawaited from onClick, and a rejection there becomes an
			// unhandled promise rejection.
			console.error('[DoodleCardNode] Failed to attach doodle', error);
			resetAttachState();
			showAttachError();
		}
	};

	return (
		<Box my={2}>
			<DoodlePreview strokes={strokes} onClick={isReadOnly ? undefined : doodleModal.onOpen} ariaLabel={t('doodle.edit_cta')} />
			{!isReadOnly && (
				<Box mt={2} display="flex" gap={2} flexWrap="wrap">
					<Button
						size="xs"
						variant="ghost"
						colorScheme="secondary"
						color="text.soft"
						leftIcon={<ScribbleIcon />}
						onClick={doodleModal.onOpen}
						aria-label={t('doodle.edit_cta')}
					>
						{t('doodle.edit_cta')}
					</Button>
					<Button
						size="xs"
						colorScheme="primary"
						onClick={() => {
							void handleAddToRecognition();
						}}
						isLoading={attachPending}
						isDisabled={attached}
						aria-label={attached ? t('doodle.added') : t('doodle.add_to_draft')}
					>
						{attached ? t('doodle.added') : t('doodle.add_to_draft')}
					</Button>
				</Box>
			)}
			{doodleModal.isOpen && (
				// Mounted only while open so the editor re-seeds from the latest
				// strokes each time (useDoodle seeds initialStrokes lazily).
				<DoodleModal isOpen onClose={doodleModal.onClose} onSave={handleSaveEdit} initialStrokes={strokes} />
			)}
		</Box>
	);
}

const ScribbleIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="14"
		height="14"
		fill="none"
		stroke="currentColor"
		strokeWidth="19"
		strokeLinecap="round"
		strokeLinejoin="round"
		viewBox="0 0 256 256"
		aria-hidden="true"
	>
		<path d="M32,92.8S64,48,112,48c104,0,92.6,160-3.4,160-64,0-64-96,32-96,64,0,107.4,48,107.4,48" />
	</svg>
);
